import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const selectedSwatch = this.closest('.shopify-section, dialog')?.querySelector(
      '.plwc-custom-swatch.is-selected[data-media-id]'
    );
    const requestedMediaId = selectedSwatch?.dataset.mediaId;
    const requestedMediaSource =
      selectedSwatch?.querySelector('img')?.currentSrc || selectedSwatch?.querySelector('img')?.src;
    const selectedMediaId = requestedMediaId || event.detail.resource?.featured_media?.id;

    // PLWC displays every product and color image in the carousel. For the
    // same product, replacing the whole gallery is unnecessary and creates a
    // race with Canyon's slideshow initialization. Keep the current gallery
    // and select the requested color in place.
    if (this.dataset.hideVariants !== 'true' && !event.detail.data.newProduct) {
      requestAnimationFrame(() => {
        if (selectedMediaId && this.selectMediaById(selectedMediaId)) return;
        this.selectMediaByImage(requestedMediaSource);
      });
      return;
    }

    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    const currentColorInfo = this.querySelector('[data-plwc-gallery-color-info]');
    const newColorInfo = newMediaGallery.querySelector('[data-plwc-gallery-color-info]');
    const selectedIndex = getMediaIndex(newMediaGallery, selectedMediaId, requestedMediaSource);

    // Keep the selected color description when Canyon swaps in the newly
    // rendered gallery.
    if (currentColorInfo && newColorInfo && !currentColorInfo.hidden) {
      newColorInfo.innerHTML = currentColorInfo.innerHTML;
      newColorInfo.hidden = false;
    }

    // Give Canyon the correct initial slide before the replacement slideshow
    // connects. Otherwise its delayed setup resets the gallery to slide zero.
    if (selectedIndex >= 0) {
      newMediaGallery
        .querySelector('slideshow-component')
        ?.setAttribute('initial-slide', String(selectedIndex));
    }

    this.replaceWith(newMediaGallery);

    // The server-rendered gallery always opens on its first slide. After a
    // shopper selects a variant, move the replacement gallery to that
    // variant's featured media instead of allowing it to jump back to the
    // product's stock photo.
    if (!selectedMediaId && !requestedMediaSource) return;

    const confirmSelectedMedia = () => {
      if (!newMediaGallery.isConnected) return;
      if (selectedMediaId && newMediaGallery.selectMediaById(selectedMediaId)) return;
      newMediaGallery.selectMediaByImage(requestedMediaSource);
    };

    // Canyon finishes slideshow setup on a nested animation frame. Confirming
    // once afterward prevents that setup from returning to the stock photo.
    requestAnimationFrame(() => requestAnimationFrame(confirmSelectedMedia));
    window.setTimeout(confirmSelectedMedia, 120);
  };

  /**
   * Selects the gallery slide containing a Shopify media ID.
   * @param {string | number} mediaId
   */
  selectMediaById(mediaId) {
    if (!mediaId) return false;

    const selectedMedia = this.querySelector(`.product-media[data-media-id="${mediaId}"]`);
    const selectedSlide = selectedMedia?.closest('slideshow-slide');

    if (!selectedSlide) return false;

    const slides = Array.from(this.querySelectorAll('slideshow-slide'));
    const selectedIndex = slides.indexOf(selectedSlide);

    if (selectedIndex < 0) return false;

    this.slideshow?.select(selectedIndex, undefined, { animate: false });
    return true;
  }

  /**
   * Selects a slide by matching the Shopify image filename. This covers
   * colors whose option swatch has an image but whose variant is missing a
   * featured-media assignment.
   * @param {string} source
   */
  selectMediaByImage(source) {
    const requestedFilename = getImageFilename(source);
    if (!requestedFilename) return false;

    const selectedImage = Array.from(this.querySelectorAll('slideshow-slide .product-media img')).find(
      (image) => getImageFilename(image.currentSrc || image.src) === requestedFilename
    );
    const selectedSlide = selectedImage?.closest('slideshow-slide');

    if (!selectedSlide) return false;

    const slides = Array.from(this.querySelectorAll('slideshow-slide'));
    const selectedIndex = slides.indexOf(selectedSlide);

    if (selectedIndex < 0) return false;

    this.slideshow?.select(selectedIndex, undefined, { animate: false });
    return true;
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}

function getImageFilename(source) {
  if (!source) return '';

  try {
    return decodeURIComponent(new URL(source, window.location.href).pathname.split('/').pop() || '')
      .replace(/_(?:\d+x\d+|\d+x|x\d+)(?=\.[^.]+$)/, '')
      .toLowerCase();
  } catch (error) {
    return '';
  }
}

function getMediaIndex(gallery, mediaId, source) {
  const slides = Array.from(gallery.querySelectorAll('slideshow-slide'));
  let selectedSlide = null;

  if (mediaId) {
    selectedSlide = gallery
      .querySelector(`.product-media[data-media-id="${mediaId}"]`)
      ?.closest('slideshow-slide');
  }

  if (!selectedSlide) {
    const requestedFilename = getImageFilename(source);

    if (requestedFilename) {
      selectedSlide = Array.from(gallery.querySelectorAll('slideshow-slide .product-media img'))
        .find((image) => getImageFilename(image.currentSrc || image.src) === requestedFilename)
        ?.closest('slideshow-slide');
    }
  }

  return selectedSlide ? slides.indexOf(selectedSlide) : -1;
}

// PLWC's custom picker sits outside Canyon's native variant picker. Select
// its media immediately on the first click; the variant-update handler above
// then preserves the same slide when Canyon replaces the gallery.
if (!window.plwcGalleryMediaClickHandlerInstalled) {
  window.plwcGalleryMediaClickHandlerInstalled = true;

  document.addEventListener('click', (event) => {
    const swatch = event.target.closest('.plwc-custom-swatch[data-media-id]');
    const mediaId = swatch?.dataset.mediaId;
    const mediaSource = swatch?.querySelector('img')?.currentSrc || swatch?.querySelector('img')?.src;

    if (!mediaId && !mediaSource) return;

    const gallery = swatch.closest('.shopify-section, dialog')?.querySelector('media-gallery');

    if (mediaId && gallery?.selectMediaById(mediaId)) return;
    gallery?.selectMediaByImage(mediaSource);
  });
}
